import React, { FC, useCallback } from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';

// Components
import Loading from '@components/loading';
import AnimatedTable from '@components/settings/components/common/animatedTable';
import ColorWrapper from '@styles/colors';
import EventErrorDisplay from './components/EventErrorDisplay';
import { CellRenderer } from '@components/settings/components/common/animatedTable/types';

// Types & Interfaces
import { NcFileBatchEventType } from '@v2/pages/common/enums';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { ColorFunction } from '@styles/colors/types';
import { NCFileBatchEvent } from './types';

// Helpers & Utils
import { isTabletOrWeb } from '@helpers';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { getEventKeyValue, getRowKey } from './helpers';

// Hooks
import { useManageNCFileBatchEvents } from './viewmodels/useManageNCFileBatchEvents';
import { useEventBarChart } from './viewmodels/useEventBarChart';
import { useTableColumns } from './viewmodels/useTableColumns';
import { NCFileBatchEventsCellRenderer } from './components/CellRenderer';

import { SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL, SCREEN_PADDING_VERTICAL_FOR_ITEM, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { NCFileBatchEventsMobile } from './components/NCFileBatchEventsMobile';
import TraceEventPopup from './components/TraceEventPopup';
import { MachineEventSearchItem } from '@interfaces/machines';
import { DEFAULT_EVENT_KEY_FIELDS, EVENT_POPUP_STYLES } from './constants';
import SidePanel from '@v2/pages/common/components/SidePanel';
import { useSidePanelSelection } from '@v2/pages/common/components/SidePanel/hooks/useSidePanelSelection';
import TimeTiles from './components/TimeTiles';
import { renderAccordionContent, getAccordionItemsCount } from './components/AccordionContent';
import { CurveLinesProvider, CurveLinesContainer } from './context/CurveLinesContext';
import CurveLines from './components/ConnectorLines';
import I18n from 'i18n-js';
import { DelayedEventsText } from './components/DelayedEventsText';
import TimeTilesMobile from './components/TimeTilesMobile';

export interface NCFileBatchEventsProps {
  eventType: NcFileBatchEventType;
  C: ColorFunction;
  ncFileId?: number | null;
  productionBatchId?: number | null;
  machineIds?: number[];
  scale?: ScaleFunction;
  font?: typeof initialFont;
  startedOn?: string;
  endedOn?: string;
  webTabletContainerStyle?: StyleProp<ViewStyle>;
  withoutTimeTiles?: boolean;
  customOrderBy?: string;
}

const NCFileBatchEvents: FC<NCFileBatchEventsProps> = ({
  eventType,
  ncFileId,
  productionBatchId,
  C,
  webTabletContainerStyle,
  machineIds,
  startedOn,
  endedOn,
  withoutTimeTiles,
  customOrderBy,
  scale = initialScale,
  font = initialFont,
}) => {
  const {
    events,
    isLoading,
    isLoadingMore,
    error,
    orderBy,
    orderByConfig,
    textConfig,
    shouldDisplayQuantityDelta,
    shouldDisplayNCFileName,
    shouldDisplayQuantity,
    shouldDisplayBatch,
    shouldDisplayMachine,
    shouldShowBarChart,
    shouldDisplayStatusSubStatusExecutionMode,
    shouldDisplayAccordionTrigger,
    shouldDisplayVersion,
    getNextPage,
    handleSort,
    ncFileStatistics,
    expandedRows,
    toggleAccordion,
    eventsPerspective,
    eventsTableRef,
    handleOptimisticUpdate,
    refetchStatistics,
    shouldShowTimeTilesAndCurveLines,
  } = useManageNCFileBatchEvents({
    eventType,
    ncFileId,
    productionBatchId,
    machineIds,
    startedOn,
    endedOn,
    withoutTimeTiles,
    customOrderBy,
  });

  // // Get timeline data calculator and marker positions
  const { barChartData } = useEventBarChart({
    eventType,
    eventsPerspective,
    events,
    ncFileStatistics,
  });

  const firstElementKeyValue = getEventKeyValue(events[0] ?? null);

  const { selected: selectedEvent, isOpen, select: handleEventSelect, close: handleEventDeselect, requestClose, cancelPendingClose } = useSidePanelSelection<MachineEventSearchItem>();

  const {
    TABLE_CONFIG,
    tableColumns,
    toggleAccordion: handleAccordionToggle,
  } = useTableColumns({
    C,
    barChartData,
    eventType,
    orderBy,
    orderByConfig,
    productionBatchId,
    eventsPerspective,
    shouldDisplayQuantityDelta,
    shouldDisplayVersion,
    shouldDisplayNCFileName,
    shouldDisplayBatch,
    shouldDisplayMachine,
    shouldDisplayQuantity,
    shouldShowBarChart,
    shouldDisplayStatusSubStatusExecutionMode,
    shouldDisplayAccordionTrigger,
    textConfig,
    font,
    scale,
    expandedRows, // Redux Record
    toggleAccordion, // Redux-integrated wrapper
    firstElementKeyValue,
  });

  // Optimize event row click handler with useCallback
  const handleEventPress = useCallback(
    (item: MachineEventSearchItem) => {
      // Handle trace event popup
      if (eventType === NcFileBatchEventType.TRACE) {
        handleEventSelect(item);
        return;
      }

      // Handle accordion toggle for non-trace events
      // No need to check isMainRow - all items in array are main rows
      if (handleAccordionToggle) {
        const rowKey = getRowKey(eventType, item);

        // Extract parameters based on event type
        if (eventType === NcFileBatchEventType.SETUP && item.productionBatchId != null) {
          handleAccordionToggle({
            rowKey,
            sequenceNumbers: undefined,
            productionBatchId: item.productionBatchId,
          });
        } else {
          const sequenceNumbers: number[] = item.machineEventSequenceNumbers || [];
          if (sequenceNumbers.length > 0) {
            handleAccordionToggle({
              rowKey,
              sequenceNumbers,
              machineId: item.machineId,
            });
          }
        }
      }
    },
    [eventType, handleAccordionToggle]
  );

  const handleSubRowPress = useCallback(
    (item: MachineEventSearchItem) => {
      handleEventSelect(item);
    },
    [handleEventSelect]
  );

  // Optimize load handler with useCallback
  const handleLoad = useCallback(
    (params?: { reset: boolean; orderBy: Record<string, string> }) => {
      if (isLoadingMore) return undefined;

      // If orderBy is provided, it's a sort change
      if (params?.orderBy) {
        return handleSort(params);
      }

      // Otherwise it's pagination
      return getNextPage();
    },
    [isLoadingMore, getNextPage, handleSort]
  );

  // Optimize end reached handler with useCallback
  // Check both isLoading (initial load) and isLoadingMore (pagination) to prevent unwanted calls
  const handleEndReached = useCallback(() => {
    if (!isLoading && !isLoadingMore) {
      getNextPage();
    }
  }, [isLoading, isLoadingMore, getNextPage]);

  // Create stable cellRenderer callback
  const cellRenderer = useCallback<CellRenderer<NCFileBatchEvent>>((column, item, props) => NCFileBatchEventsCellRenderer(column, item, props), []);

  // Determine if component should render table
  const shouldRenderTable = isTabletOrWeb;

  // Error state
  if (error) {
    return (
      <View style={styles.container}>
        <EventErrorDisplay error={error} C={C} scale={scale} font={font} />
      </View>
    );
  }

  // Mobile view (non-web)
  if (!shouldRenderTable) {
    return (
      <View style={{ flex: 1 }}>
        <TimeTilesMobile eventTimelineData={barChartData?.[0]} />
        <DelayedEventsText C={C} containerStyle={{ marginTop: scale(12), marginBottom: scale(3), alignItems: 'flex-end' }} />
        <NCFileBatchEventsMobile
          {...{
            isLoading,
            C,
            events,
            eventType,
            eventsPerspective,
            barChartData,
            isLoadingMore,
            shouldDisplayNCFileName,
            shouldDisplayBatch,
            shouldDisplayMachine,
            shouldDisplayQuantity,
            shouldDisplayQuantityDelta,
            shouldDisplayVersion,
            shouldDisplayStatusSubStatusExecutionMode,
            shouldDisplayAccordionTrigger,
            shouldShowBarChart,
            textConfig,
            font,
            scale,
            expandedRows,
            toggleAccordion,
            onEndReached: handleEndReached,
            onEndReachedThreshold: 0.5,
            containerStyle: { marginHorizontal: -scale(SCREEN_PADDING_HORIZONTAL) },
            keyExtractorFields: DEFAULT_EVENT_KEY_FIELDS,
          }}
        />
      </View>
    );
  }

  const TableContent = (
    <View ref={eventsTableRef} style={[styles.container, webTabletContainerStyle]}>
      <AnimatedTable
        borderRadiusConfig={{ borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS), borderTopRightRadius: scale(SCREEN_BORDER_RADIUS) }}
        C={C}
        loaded={!isLoading}
        array={events}
        load={handleLoad}
        onPress={handleEventPress}
        onPressIn={cancelPendingClose}
        onSubRowPress={handleSubRowPress}
        onSubRowPressIn={cancelPendingClose}
        config={tableColumns}
        cellRenderer={cellRenderer}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.5}
        stickyHeader
        selectedItem={selectedEvent}
        getIsSelectedRow={(item, selectedItem) => selectedItem != null && item.sequenceNumber === selectedItem.sequenceNumber}
        getHasOverlay={(item, selectedItem) => {
          if (selectedItem?.__isSubRowForColumnConfig) return false;
          return selectedItem != null && item.sequenceNumber !== selectedItem.sequenceNumber;
        }}
        enableAccordion={shouldDisplayAccordionTrigger}
        autoScrollOnExpand
        getRowKey={(item) => getRowKey(eventType, item)}
        getRowTestID={(_item, index) => TEST_IDS.ncFileBatchEvents.row(index)}
        renderAccordionContent={renderAccordionContent}
        getAccordionItemsCount={getAccordionItemsCount}
        expandedRows={expandedRows}
        {...TABLE_CONFIG}
      />
      {isLoadingMore && (
        <View style={{ flex: 1, width: '100%', position: 'absolute', flexDirection: 'row', justifyContent: 'center', bottom: scale(10) }}>
          <Loading background="transparent" />
        </View>
      )}
      <SidePanel
        isOpen={isOpen}
        onClose={requestClose}
        width={EVENT_POPUP_STYLES.width}
        scale={scale}
      >
        {selectedEvent ? <TraceEventPopup onClose={handleEventDeselect} event={selectedEvent} /> : null}
      </SidePanel>
    </View>
  );

  if (!shouldShowTimeTilesAndCurveLines) {
    return TableContent;
  }

  return (
    <CurveLinesProvider>
      <CurveLinesContainer style={[styles.container, { padding: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM) }]}>
        <TimeTiles
          eventTimelineData={barChartData?.[0]}
          eventType={eventType}
          eventsPerspective={eventsPerspective}
          ncFileId={ncFileId}
          onOptimisticUpdate={handleOptimisticUpdate}
          onOptimisticUpdateError={refetchStatistics}
          isDataLoaded={!isLoading}
        />
        <DelayedEventsText C={C} containerStyle={{ marginTop: scale(17), marginBottom: scale(3), alignItems: 'flex-end' }} />
        <CurveLines C={C} markerYOffset={TABLE_CONFIG?.headerSize} />
        {TableContent}
      </CurveLinesContainer>
    </CurveLinesProvider>
  );
};

// Styles
const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default ColorWrapper(NCFileBatchEvents);
